import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Trophy,
  TrendingUp,
  Target,
  CheckCircle,
  XCircle,
  ChevronRight,
  Zap,
  Settings,
  FileText,
} from 'lucide-react';
import Logo from '@/components/shared/Logo.tsx';
import ThemeToggle from '@/components/shared/ThemeToggle.tsx';
import LanguageSelector from '@/components/shared/LanguageSelector.tsx';
import {
  useScrollReveal,
  useCountUp,
  useActiveSection,
} from '@/components/landing/hooks';
import { scrollToSection } from '@/components/landing/sections';
import ScrollNavigator from '@/components/landing/ScrollNavigator';
import LandingSection from '@/components/landing/LandingSection';
import HowItWorksStep from '@/components/landing/HowItWorksStep';
import MockupTeamDashboard from '@/components/landing/mockups/MockupTeamDashboard';
import MockupConfigurator from '@/components/landing/mockups/MockupConfigurator';
import MockupReport from '@/components/landing/mockups/MockupReport';

export default function LandingPage() {
  const { t } = useTranslation();
  const containerRef = useScrollReveal();
  const activeIndex = useActiveSection();

  const countRef1 = useCountUp(3.3, 1, '×');
  const countRef2 = useCountUp(75, 0, '%');
  const countRef3 = useCountUp(30, 0, '%');

  return (
    <div ref={containerRef} className="min-h-screen bg-card">
      {/* ── Navbar ── */}
      <nav className="sticky top-0 z-40 border-b border-border bg-card/80 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Logo size="md" linkTo="/" />

          <div className="hidden items-center gap-6 md:flex">
            {[
              { targetId: 'paso-1', labelKey: 'landing.navHowItWorks', activeFor: [1, 2, 3] },
              { targetId: 'beneficios', labelKey: 'landing.navBenefits', activeFor: [4] },
              { targetId: 'datos', labelKey: 'landing.navData', activeFor: [5] },
            ].map((link) => {
              const isActive = link.activeFor.includes(activeIndex);
              return (
                <button
                  key={link.targetId}
                  onClick={() => scrollToSection(link.targetId)}
                  className={`text-sm transition-colors ${
                    isActive
                      ? 'font-medium text-primary'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {t(link.labelKey)}
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-2">
            <LanguageSelector />
            <ThemeToggle />
            <Link
              to="/login"
              className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-hover transition-colors"
            >
              {t('landing.loginButton')}
            </Link>
          </div>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section id="hero" className="flex min-h-[calc(100vh-4rem)] items-center">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h1
              className="font-display font-[800] text-foreground"
              style={{ fontSize: 'clamp(36px, 6vw, 72px)', lineHeight: 1.08, letterSpacing: '-1.5px' }}
            >
              {t('landing.heroTitle_1')}{' '}
              <span className="text-gradient-cierre">{t('landing.heroTitle_accent')}</span>
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground">
              {t('landing.heroDescription')}
            </p>
            <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                to="/register"
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-8 py-3 text-base font-semibold text-white hover:bg-primary-hover transition-colors"
              >
                {t('landing.ctaStart')}
                <ChevronRight className="h-4 w-4" />
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 rounded-lg border border-primary px-8 py-3 text-base font-semibold text-primary hover:bg-primary/5 transition-colors"
              >
                {t('landing.ctaHaveAccount')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Cómo funciona — Step 1 ── */}
      <LandingSection id="paso-1" topBorder bg="muted">
        <div className="landing-fade-up mx-auto mb-12 max-w-2xl text-center">
          <h2 className="text-3xl font-heading text-foreground sm:text-4xl">{t('landing.howItWorksTitle')}</h2>
          <p className="mt-4 text-muted-foreground">
            {t('landing.howItWorksSubtitle')}
          </p>
        </div>
        <HowItWorksStep
          number={1}
          titleKey="landing.step1Title"
          descKey="landing.step1Desc"
          featureKey="landing.step1Feature"
          featureIcon={Trophy}
          mockup={<MockupTeamDashboard />}
          mockupMaxWidth="md"
        />
      </LandingSection>

      {/* ── Cómo funciona — Step 2 ── */}
      <LandingSection id="paso-2" bg="muted">
        <HowItWorksStep
          number={2}
          titleKey="landing.step2Title"
          descKey="landing.step2Desc"
          featureKey="landing.step2Feature"
          featureIcon={Settings}
          mockup={<MockupConfigurator />}
          mockupMaxWidth="sm"
          reverse
        />
      </LandingSection>

      {/* ── Cómo funciona — Step 3 ── */}
      <LandingSection id="paso-3" bg="muted">
        <HowItWorksStep
          number={3}
          titleKey="landing.step3Title"
          descKey="landing.step3Desc"
          featureKey="landing.step3Feature"
          featureIcon={FileText}
          mockup={<MockupReport />}
          mockupMaxWidth="md"
        />
      </LandingSection>

      {/* ── Problema + Solución ── */}
      <LandingSection id="beneficios" topBorder>
        <div className="landing-fade-up mx-auto mb-16 max-w-2xl text-center">
          <h2 className="text-3xl font-heading text-foreground sm:text-4xl">
            {t('landing.problemTitle')}
          </h2>
        </div>

        <div className="landing-fade-up grid gap-6 lg:grid-cols-2">
          {[
            {
              titleKey: 'landing.withoutRPL',
              items: [t('landing.pain1'), t('landing.pain2'), t('landing.pain3'), t('landing.pain4')],
              Icon: XCircle,
              iconClass: 'text-destructive',
            },
            {
              titleKey: 'landing.withRPL',
              items: [t('landing.solution1'), t('landing.solution2'), t('landing.solution3'), t('landing.solution4')],
              Icon: CheckCircle,
              iconClass: 'text-green-500',
            },
          ].map((col) => (
            <div key={col.titleKey} className="rounded-xl border border-border bg-muted p-6">
              <h3 className="mb-5 text-lg font-semibold text-foreground">{t(col.titleKey)}</h3>
              <div className="space-y-4">
                {col.items.map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <col.Icon className={`mt-0.5 h-5 w-5 shrink-0 ${col.iconClass}`} />
                    <span className="text-sm text-muted-foreground">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </LandingSection>

      {/* ── Datos del sector ── */}
      <LandingSection id="datos" topBorder bg="muted">
        <div className="landing-fade-up mx-auto mb-16 max-w-2xl text-center">
          <h2 className="text-3xl font-heading text-foreground sm:text-4xl">
            {t('landing.dataTitle')}
          </h2>
          <p className="mt-4 text-muted-foreground">
            {t('landing.dataSubtitle')}
          </p>
        </div>

        <div className="landing-fade-up grid gap-6 sm:grid-cols-3">
          {[
            { Icon: Zap, countRef: countRef1, initial: '0×', labelKey: 'landing.stat1Label' },
            { Icon: Target, countRef: countRef2, initial: '0%', labelKey: 'landing.stat2Label' },
            { Icon: TrendingUp, countRef: countRef3, initial: '0%', labelKey: 'landing.stat3Label' },
          ].map((stat) => (
            <div key={stat.labelKey} className="rounded-xl border border-border bg-card p-6 text-center">
              <stat.Icon className="mx-auto mb-3 h-8 w-8 text-primary" />
              <p className="text-4xl font-extrabold text-foreground">
                <span ref={stat.countRef}>{stat.initial}</span>
              </p>
              <p className="mt-2 text-sm font-medium text-muted-foreground">
                {t(stat.labelKey)}
              </p>
            </div>
          ))}
        </div>
      </LandingSection>

      {/* ── CTA final ── */}
      <section id="cta-final" className="flex min-h-[calc(100vh-4rem)] items-center border-t border-border">
        <div className="landing-fade-up mx-auto w-full max-w-2xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-3xl font-heading text-foreground sm:text-4xl">
            {t('landing.ctaFinalTitle')}
          </h2>
          <p className="mx-auto mt-4 max-w-lg text-muted-foreground">
            {t('landing.ctaFinalDesc')}
          </p>
          <Link
            to="/register"
            className="mt-8 inline-flex items-center gap-2 rounded-lg bg-primary px-10 py-3.5 text-base font-semibold text-white hover:bg-primary-hover transition-colors"
          >
            {t('landing.ctaFinalButton')}
            <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <ScrollNavigator activeIndex={activeIndex} />

      {/* ── Footer ── */}
      <footer className="border-t border-border bg-muted">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-4 py-8 sm:flex-row sm:px-6 lg:px-8">
          <Logo size="sm" linkTo="/" />
          <p className="text-xs text-muted-foreground">{t('landing.copyright')}</p>
        </div>
      </footer>
    </div>
  );
}
